import { Box } from "@mui/material";
import Link from "next/link";
import { useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_MEDIA,
  SITE_TYPE,
  SITE_VAR,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import { revealRef } from "./motion/reveal";
import { SITE_BUTTON_STYLES } from "./siteButtonStyles";
import { SiteSection } from "./SiteSection";
import { siteTelFor } from "./siteTel";
import { SiteTitle } from "./SiteText";

const CLOSING_STYLES = {
  box: {
    background: SITE_COLOR.panel,
    borderRadius: SITE_VAR.radiusImage,
    padding: "clamp(40px,7vw,96px)",
    display: "grid",
    gridTemplateColumns: "minmax(0,1.25fr) minmax(0,1fr)",
    gap: "clamp(40px,6vw,96px)",
    alignItems: "end",
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "1fr" },
  },
  eyebrow: SITE_TYPE.eyebrow,
  title: { ...SITE_TYPE.h2, maxWidth: "14em" },
  label: { fontSize: "18px", fontWeight: 600, color: SITE_COLOR.ink2 },
  tel: {
    fontSize: "30px",
    lineHeight: "39px",
    fontWeight: 600,
    color: SITE_COLOR.ink,
    fontVariantNumeric: "tabular-nums",
  },
  hours: {
    marginTop: "12px",
    fontSize: "14px",
    lineHeight: 1.7,
    color: SITE_COLOR.ink2,
  },
  actions: { display: "flex", gap: "8px", marginTop: "28px", flexWrap: "wrap" },
} as const;

export interface ClosingContactProps {
  id?: string;
  /** 머리말(시안 「DroneSquare 1:1 문의」) */
  eyebrow: ReactNode;
  title: ReactNode;
  /** 바깥 구역 스타일 덮어쓰기(마지막 구역의 아래 여백 등) */
  sectionSx?: object;
  /** 버튼 묶음 — 없으면 「문의하기」(`/inquiry`) 하나 */
  actions?: ReactNode;
}

/** 문의 마무리 구역(시안 `.closing`): 왼쪽 제목, 오른쪽 대표전화·운영시간·「문의하기」(`/inquiry`). */
export function ClosingContact({
  id,
  eyebrow,
  title,
  sectionSx,
  actions,
}: ClosingContactProps) {
  const { t, i18n } = useTranslation("landing");
  const titleId = useId();
  const hours = t("site.contact.hoursLines", {
    returnObjects: true,
  }) as readonly string[];
  return (
    <SiteSection
      id={id}
      labelledBy={titleId}
      sx={[{ paddingTop: 0 }, sectionSx ?? {}]}
    >
      <Box sx={SITE_WRAP}>
        <Box ref={revealRef} data-reveal="up" sx={CLOSING_STYLES.box}>
          <div>
            <Box component="p" sx={CLOSING_STYLES.eyebrow}>
              {eyebrow}
            </Box>
            <Box component="h2" id={titleId} sx={CLOSING_STYLES.title}>
              {typeof title === "string" ? <SiteTitle text={title} /> : title}
            </Box>
          </div>
          <dl>
            <Box component="dt" sx={CLOSING_STYLES.label}>
              {t("site.contact.telLabel")}
            </Box>
            <Box component="dd" sx={CLOSING_STYLES.tel}>
              {siteTelFor(i18n.language)}
            </Box>
            <Box component="dd" sx={CLOSING_STYLES.hours}>
              {hours.map((line, index) => (
                <span key={line}>
                  {index > 0 && <br />}
                  {line}
                </span>
              ))}
            </Box>
            <Box component="dd" sx={CLOSING_STYLES.actions}>
              {actions ?? (
                <Box
                  component={Link}
                  href="/inquiry"
                  sx={SITE_BUTTON_STYLES.primaryLarge}
                >
                  {t("site.contact.inquiry")}
                </Box>
              )}
            </Box>
          </dl>
        </Box>
      </Box>
    </SiteSection>
  );
}
